import { Checkbox, Icon, IconButton, ISelection, Stack, TooltipHost, Selection, ShimmeredDetailsList, SelectionMode, IColumn, ActionButton, CommandBar, ICommandBarItemProps } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';

// assets

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import { faFile, faLock } from '@fortawesome/pro-light-svg-icons';
import GeneralService from '../../../../services/general';
import { IDocumentProps } from '../../../../props/general';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import LoadingDialogComponent from '../../../feedbacks/loadingDialog';
import RenderImage from '../renderImage';
import Tag from '../../tag';
import DocumentService from '../../../../services/documents/document';
import FilePreview from '../preview';
import PivotButton from '../../buttons/pivot';
import { getFileTypeIconProps } from '@fluentui/react-file-type-icons';
import DocumentTypes from '../../../../manifests/documentTypes';
import FileService from '../../../../services/general/file';
import DocumentVisibilities from '../../../../manifests/documentVisibilities';

export type TTagVariant = 'default' | 'active' | 'success' | 'light' | 'error' | 'warning' | 'transparent';

export type TDocumentModeView = 'list' | 'card';
export type TExpiryStatus = 'all' | 'expired' | 'notExpired';

type IFilesListProps = {
    files: IDocumentProps[];
    selected?: IDocumentProps[];
    loaded?: boolean;
    mode?: TDocumentModeView;
    expiryStatus?: TExpiryStatus;
    onSelectFile?(selected?: IDocumentProps[]): void;
    hideCommandBar?: boolean;
    hideSelectViewButton?: boolean;
    size?: 'small' | 'medium' | 'large';
    hideTag?: boolean;
    hideVisibility?: boolean;
    documentTypes?: {
        key: string;
        text: string;
    }[];
}

const FilesList: React.FC<IFilesListProps> = (props: IFilesListProps) => {
    const { banner } = useStore();

    const inputDocumentRef = React.useRef<HTMLInputElement>(null);
    const [files, setFiles] = React.useState<IDocumentProps[]>(props.files);
    const [selected, setSelected] = React.useState<IDocumentProps[]>(props.selected || []);
    const [loaded, setLoaded] = React.useState<boolean>(props.loaded || false);
    const [downloading, setDownloading] = React.useState<boolean>(false);

    const [mode, setMode] = useState<TDocumentModeView>(props.mode || 'list');
    const [expiryStatus, setExpiryStatus] = useState<TExpiryStatus>(props.expiryStatus || 'all');

    const [selectedPreview, setSelectedPreview] = useState<IDocumentProps>();
    const [activeSurface, setActiveSurface] = useState<string | undefined>();

    const size = props.size || 'large';
    let gap = 20;
    if (size === 'medium') {
        gap = 15;
    } else if (size === 'small') {
        gap = 10;
    }

    const selection: ISelection = new Selection({
        onSelectionChanged: () => {
            setSelected(selection.getSelection() as IDocumentProps[])
            props.onSelectFile && props.onSelectFile(selection.getSelection() as IDocumentProps[])
        },
    });

    useEffect(() => {
        setMode(props.mode || 'list')
        return () => { }
    }, [props.mode])

    useEffect(() => {
        setFiles(props.files);
    }, [props.files]);

    useEffect(() => {
        setLoaded(props.loaded || false);
    }, [props.loaded]);

    useEffect(() => {
        setSelected(props.selected || []);
    }, [props.selected]);

    const _onPreview = (file: IDocumentProps) => {
        setSelectedPreview(file);
        setActiveSurface('preview');
    }

    const _onClick = async (file: IDocumentProps) => {
        let _selected = selected;
        const idx = _selected.findIndex((sel) => sel.path === file.path);
        if (idx > -1) {
            _selected.splice(idx, 1);
        } else {
            _selected.push(file);
        }

        if (props.onSelectFile) {
            props.onSelectFile(_selected);
        } else {
            setSelected([..._selected]);
        }
    }

    const _onDownload = async (file: IDocumentProps) => {
        try {
            setDownloading(true);
            await FileService.download(file);
            setDownloading(false);
        } catch (error) {
            setDownloading(false);
            console.log(error);
        }
    }

    const renderFileCard = (file: IDocumentProps) => {

        const type = GeneralService.getFileType(file.basename);
        const documentType = (props.documentTypes || []).find(f => (f.key || "").toLowerCase() === (file.type || "").toLowerCase());
        const isSelected = selected.find((sel) => sel.path === file.path) !== undefined;

        return <Stack key={file.basename} className={`${styles.card} ${styles[size]}`}>
            <Checkbox onChange={() => _onClick(file)} checked={isSelected} className={`${styles.checkbox} ${isSelected ? styles.selected : ''}`} styles={{ checkbox: { borderRadius: '50%', background: isSelected ? undefined : '#fff', width: 25, height: 25 } }} />
            <IconButton
                className={styles.download}
                iconProps={{ iconName: "Download" }}
                onClick={() => _onDownload(file)}
            />
            <Stack onClick={() => _onPreview(file)} className={`${styles.fileImage} ${styles["bg" + type]}`}>
                {type === 'word' ? <Icon className={styles.icon} iconName='WordDocument' /> : null}
                {type === 'excel' ? <Icon className={styles.icon} iconName='ExcelDocument' /> : null}
                {type === 'powerpoint' ? <Icon className={styles.icon} iconName='PowerPointDocument' /> : null}
                {type === 'pdf' ? <Icon className={styles.icon} iconName='PDF' /> : null}
                {type === 'video' ? <Icon className={styles.icon} iconName='Video' /> : null}
                {type === 'file' ? <FontAwesomeIcon className={styles.icon} icon={faFile} /> : null}
                {type === 'image' ? <RenderImage path={file.path} className={styles.image} /> : null}
            </Stack>
            <Stack className={styles.content} onClick={() => _onPreview(file)} tokens={{ childrenGap: 3 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign={'space-between'} verticalAlign='center'>
                    {file.uploadedAt ? <Text size={'xsmall'}>{moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")}</Text> : null}
                    {size !== 'small' ? <Text size={'xsmall'}>{GeneralService.getFriendlyFileSizeText(file.size, true)}</Text> : null}
                    {size !== 'small' ? <Stack horizontal tokens={{ childrenGap: 5 }}>
                        {props.hideTag ? null : <Tag variant='default'>
                            <Stack>
                                <Text size={'xsmall'}>{documentType ? documentType.text : file.type}</Text>
                            </Stack>
                        </Tag>}
                        {props.hideVisibility || file.visibility !== 'private' ? null : <Tag variant='default'>
                            <Stack styles={{ root: { height: '100%', width: '100%' } }} horizontalAlign='center' verticalAlign='center'>
                                <FontAwesomeIcon icon={faLock} size='2xs' />
                            </Stack>
                        </Tag>}
                    </Stack> : null}
                </Stack>
                <TooltipHost content={<Text size={'small'}>{file.basename}</Text>}>
                    <Stack className={styles.name}>
                        {size === 'large' ? <Text className={styles.text} size={'medium'}>{file.basename}</Text> : null}
                        {size === 'medium' ? <Text className={styles.text} size={'small'}>{file.basename}</Text> : null}
                        {size === 'small' ? <Text className={styles.text} size={'xsmall'}>{file.basename}</Text> : null}
                    </Stack>
                </TooltipHost>
            </Stack>
        </Stack>
    }

    const columns: IColumn[] = [
        {
            key: 'icon',
            name: 'icon',
            iconName: "Page",
            isIconOnly: true,
            minWidth: 20,
            maxWidth: 20,
            onRender: (file: IDocumentProps) => {
                const extension = ("." + file.basename.split('.').pop()).toLowerCase();
                return <Stack styles={{ root: { paddingTop: 6, paddingBottom: 6 } }}>
                    <Icon {...getFileTypeIconProps({ extension, size: 20, imageFileType: 'svg' })} />
                </Stack>
            }
        },
        {
            key: "ame",
            fieldName: "ame",
            name: "Name",
            minWidth: 150,
            onRender: (file: IDocumentProps) => {
                return <Stack.Item styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{file.basename}</Text>
                </Stack.Item>;
            }
        },
        {
            key: "type",
            fieldName: "type",
            name: "Type",
            minWidth: 175,
            maxWidth: 175,
            onRender: (file: IDocumentProps) => {
                const type = (props.documentTypes || DocumentTypes).find(f => f.key === file.type);
                return <Stack horizontal styles={{ root: { padding: '6px 0px' } }}>
                    <Tag variant='default' text={type?.text || file.type} size={'small'} />
                </Stack>;
            }
        },
        {
            key: "visibility",
            fieldName: "visibility",
            name: "Visibility",
            minWidth: 175,
            maxWidth: 175,
            onRender: (file: IDocumentProps) => {
                const visibility = (DocumentVisibilities).find(f => f.key === file.visibility);
                return <Stack.Item styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{visibility?.text || "Private"}</Text>
                </Stack.Item>;
            }
        },
        {
            key: "size",
            fieldName: "size",
            name: "Size",
            minWidth: 125,
            maxWidth: 125,
            onRender: (file: IDocumentProps) => {
                return <Stack.Item styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getFriendlyFileSizeText(file.size, true)}</Text>
                </Stack.Item>;
            }
        },
        {
            key: "expiry",
            fieldName: "expiry",
            name: "Expired Date",
            minWidth: 125,
            maxWidth: 125,
            onRender: (file: IDocumentProps) => {
                return <Stack.Item styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{file.expired_date ? GeneralService.getFriendlyDateFormat(file.expired_date) : "-"}</Text>
                </Stack.Item>;
            }
        },
        {
            key: "action",
            name: "",
            minWidth: 69,
            maxWidth: 69,
            onRender: (file: IDocumentProps) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }}>
                    <TooltipHost title={'Preview'} content={"Preview"}>
                        <IconButton iconProps={{ iconName: "View" }} onClick={() => _onPreview(file)} />
                    </TooltipHost>
                    <TooltipHost title={'Download'} content={"Download"}>
                        <IconButton iconProps={{ iconName: "Download" }} onClick={() => _onDownload(file)} />
                    </TooltipHost>
                </Stack>;
            }
        },
    ];

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        if (files.length > 0) {
            items.push({
                key: 'selectView',
                onRender: () => {
                    return <Stack styles={{ root: { marginRight: 15 } }}>
                        <PivotButton buttons={[
                            {
                                key: "list",
                                text: "List view",
                                active: mode === 'list',
                                onClick: () => {
                                    setMode('list');
                                }
                            },
                            {
                                key: "card",
                                text: "Card view",
                                active: mode === 'card',
                                onClick: () => {
                                    setMode('card');
                                }
                            }
                        ]} />
                    </Stack>
                }
            });

            items.push({
                key: 'selectExpiryStatus',
                onRender: () => {
                    return <Stack>
                        <PivotButton buttons={[
                            {
                                key: "allExpiryStatus",
                                text: "All",
                                active: expiryStatus === 'all',
                                onClick: () => {
                                    setExpiryStatus('all');
                                }
                            },
                            {
                                key: "expired",
                                text: "Expired",
                                active: expiryStatus === 'expired',
                                onClick: () => {
                                    setExpiryStatus('expired');
                                }
                            },
                            {
                                key: "notExpired",
                                text: "Not expired",
                                active: expiryStatus === 'notExpired',
                                onClick: () => {
                                    setExpiryStatus('notExpired');
                                }
                            }
                        ]} />
                    </Stack>
                }
            });
        }

        return { items, farItems };
    }

    const renderList = () => {
        const _files = files.filter((f) => {
            if (expiryStatus === 'expired') {
                return f.expired_date && moment(f.expired_date).diff(moment()) <= 0;
            } else if (expiryStatus === 'notExpired') {
                return f.expired_date && moment(f.expired_date).diff(moment()) > 0;
            } else {
                return true;
            }
        });

        return <>
            {mode === 'card' && _files.length > 0 ? <Stack horizontal tokens={{ childrenGap: gap }} wrap>
                {_files.map(renderFileCard)}
            </Stack> : null}
            {mode === 'list' && _files.length > 0 ? <Stack tokens={{ childrenGap: 20 }} wrap>
                <ShimmeredDetailsList
                    setKey="items"
                    items={_files}
                    columns={columns}
                    selectionMode={SelectionMode.multiple}
                    enableShimmer={!loaded}
                    selection={selection}
                    onShouldVirtualize={() => false}
                    ariaLabelForShimmer="Sedang mengambil data"
                    ariaLabelForGrid="Detail Item" />
            </Stack> : null}
            {_files.length < 1 ? <Text size={'small'} style={{ fontStyle: 'italic' }}>File(s) not found</Text> : null}
        </>
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {props.hideCommandBar ? null : <CommandBar styles={{ root: { padding: 0, height: 'unset', backgroundColor: 'transparent' } }} {...getCommandBarItems()} />}
        {!loaded ? <LoadingComponent label='Retrieving files ...' labelPosition='right' spinnerPosition='baseline' /> : null}
        {loaded ? renderList() : null}
        {downloading ? <LoadingDialogComponent title='Downloading File' secondaryText="Please wait while we're downloading selected file ..." /> : null}
        {activeSurface === 'preview' && selectedPreview ? <FilePreview file={selectedPreview} documentTypes={props.documentTypes} onDismissed={() => {
            setSelectedPreview(undefined);
            setActiveSurface(undefined);
        }} /> : null}
    </Stack>;
};

export default FilesList;